import { soloFecha } from './calendar';
import type { ISODate } from './types';

/** Utilidades de lectura tolerante para los adaptadores: nunca lanzan, devuelven null si el valor no sirve. */
export type Rec = Record<string, unknown>;

export const isRec = (v: unknown): v is Rec =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
export const asRec = (v: unknown): Rec => (isRec(v) ? v : {});

const NUMERIC_TEXT = /^\s*[-+]?(?:\d+\.?\d*|\.\d+)\s*$/;

/** Número finito, o texto numérico en notación JSON ("2.55"; sin heurística de miles: la API no localiza). */
export function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && NUMERIC_TEXT.test(value)) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Texto recortado y no vacío; los números se convierten a texto. */
export function toStr(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t === '' ? null : t;
}

export function toBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === 1 || value === '1') return true;
  if (value === 'false' || value === 0 || value === '0') return false;
  return fallback;
}

export const toFecha = (value: unknown): ISODate | null => soloFecha(value);

/** Instante ISO (con hora) como texto; null si no es parseable. */
export function toInstante(value: unknown): string | null {
  const t = toStr(value);
  return t !== null && !Number.isNaN(Date.parse(t)) ? t : null;
}

export function toStrList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(toStr).filter((v): v is string => v !== null))];
}

/** Primer valor no nulo entre varios alias de una clave. */
export function pick(rec: Rec, ...keys: string[]): unknown {
  for (const key of keys) {
    const v = rec[key];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/** Devuelve un objeto con las claves canónicas resueltas a partir de sus alias (el resto se conserva). */
export function resolverAlias(raw: unknown, alias: Record<string, readonly string[]>): Rec {
  const rec = asRec(raw);
  const out: Rec = { ...rec };
  for (const [canonica, nombres] of Object.entries(alias))
    out[canonica] = pick(rec, canonica, ...nombres);
  return out;
}

/**
 * Lista de elementos de una respuesta: el propio arreglo, o el primer arreglo hallado bajo alguna de `claves`.
 * null si la respuesta no contiene ninguna lista (para que el adaptador falle en vez de inventar).
 */
export function listaDe(raw: unknown, claves: readonly string[]): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (!isRec(raw)) return null;
  for (const clave of claves) {
    const v = raw[clave];
    if (Array.isArray(v)) return v;
  }
  return null;
}

/** Quita duplicados conservando la primera aparición. */
export function sinDuplicados<T>(items: readonly T[], clave: (item: T) => string): T[] {
  const vistos = new Set<string>();
  return items.filter((item) => {
    const k = clave(item);
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}
